// Scale mastery atoms: a graded scale in one key (tonic pitch-class × mode × octave
// count) as a masterable unit, derived — never a hand-kept list. A scale drill grants the
// atom; an apply piece lists the atom for its key+grade as a blocker. Pure leaf.

type ScaleMode = "major" | "minor";

const PC_NAME = [
  "c",
  "d-flat",
  "d",
  "e-flat",
  "e",
  "f",
  "g-flat",
  "g",
  "a-flat",
  "a",
  "b-flat",
  "b",
] as const;

const ATOM_RE =
  /^scale-(c|d-flat|d|e-flat|e|f|g-flat|g|a-flat|a|b-flat|b)-(major|minor)-([1-9])oct$/;

/** The atom id for a scale on `tonicMidi` with `mode`, spanning `octaves`. */
export function scaleAtomId(tonicMidi: number, mode: ScaleMode, octaves: number): string {
  const pc = (((Math.round(tonicMidi) % 12) + 12) % 12) as number;
  return `scale-${PC_NAME[pc] ?? "c"}-${mode}-${octaves}oct`;
}

/** Whether `id` is a well-formed scale-atom id. */
export function isScaleAtom(id: string): boolean {
  return ATOM_RE.test(id);
}

/** The octave count encoded in a scale-atom id, or 0 if not a scale atom. */
export function scaleOctaves(id: string): number {
  const m = ATOM_RE.exec(id);
  return m ? Number(m[3]) : 0;
}

const PRETTY_ROOT: Record<string, string> = {
  c: "C",
  "d-flat": "D♭",
  d: "D",
  "e-flat": "E♭",
  e: "E",
  f: "F",
  "g-flat": "G♭",
  g: "G",
  "a-flat": "A♭",
  a: "A",
  "b-flat": "B♭",
  b: "B",
};

/** Display name, e.g. `scale-g-major-2oct` → "G major scale (2 oct)". */
export function scaleLabel(id: string): string {
  const m = ATOM_RE.exec(id);
  if (!m) return id;
  return `${PRETTY_ROOT[m[1] ?? ""] ?? m[1]} ${m[2]} scale (${m[3]} oct)`;
}

const LETTER_PC: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

/** Parse a key string ("G major", "B-flat major", "A minor") → tonic pitch-class + mode. */
export function keyToTonicMode(key: string): { tonic: number; mode: ScaleMode } | null {
  const m = /^([a-g])(#|b|-flat|-sharp)?\s+(major|minor)$/i.exec(key.trim());
  if (!m) return null;
  const base = LETTER_PC[(m[1] ?? "").toLowerCase()];
  if (base === undefined) return null;
  const acc = (m[2] ?? "").toLowerCase();
  const delta = acc === "#" || acc === "-sharp" ? 1 : acc === "b" || acc === "-flat" ? -1 : 0;
  return { tonic: (((base + delta) % 12) + 12) % 12, mode: m[3]?.toLowerCase() as ScaleMode };
}

/** Difficulty → required scale octaves (0 = no scale blocker). Anchored to the
 *  Preparatory-rebased scale (difficulty.ts): Prep block tunes (<1.25) need no scale prereq;
 *  grade-1 broken tunes get 1 octave; grade-2+ get 2; only rung 3 fires near the grade-4 ceiling. */
export function requiredScaleRung(difficulty: number): number {
  if (difficulty < 1.25) return 0;
  if (difficulty < 2.0) return 1;
  if (difficulty < 3.25) return 2;
  if (difficulty < 4.0) return 3;
  return 4;
}
